import React, { Suspense, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Search } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { LocationData } from "@/location/location";
import { HealthStatus } from "@/installation/garyStatus";
import { useLocations } from "@/location/api";
import { useOrganizationMinimal } from "@/organization/api";
import { BellButton } from "@/components/snooze/BellButton";
import { LocationSnoozeSheetWrapper } from "@/components/snooze/LocationSnoozeSheetWrapper";
import { useAllSnoozes } from "@/hooks/useSnooze";
import MapboxMapComponent, { MapboxMapHandle } from "@/components/mapbox-map";
import { installationHealthStatusFillClass } from "@/installation/installation";

import {
  MobilePageHeader, StatusDot, StatusBadge, healthStatusToCopilot,
} from "@/components/copilot";
import type { CopilotStatus } from "@/components/copilot";

const LocationRow = ({
  location,
  isSnoozed,
  onSnoozeClick,
}: {
  location: LocationData;
  isSnoozed: boolean;
  onSnoozeClick: (loc: LocationData) => void;
}) => {
  const copilotStatus = healthStatusToCopilot(location.health_status);
  const address = [location.address_line_1, location.city, location.state].filter(Boolean).join(", ");
  const unitCount = location.installation_set?.length || 0;
  const issueCount = location.installation_set?.filter((i) => i.health_status === HealthStatus.red || i.health_status === HealthStatus.yellow).length || 0;

  return (
    <Link
      to="/organization/$organizationId/locations/$locationId"
      params={{ organizationId: String(location.organization), locationId: String(location.id) }}
      className={`flex items-center gap-[var(--space-md)] p-[var(--space-md)] bg-white border border-copilot-neutral-border rounded-[var(--card-radius)] hover:bg-copilot-surface-sunken transition-colors cursor-pointer ${isSnoozed ? "opacity-50" : ""}`}
    >
      <StatusDot status={copilotStatus} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-copilot-text-primary truncate">{location.name}</div>
        <div className="text-xs text-copilot-text-secondary mt-0.5 truncate">{address}</div>
        <div className="text-xs text-copilot-text-tertiary mt-0.5">
          {unitCount} unit{unitCount !== 1 ? "s" : ""} monitored
          {issueCount > 0 && <> · {issueCount} active issue{issueCount !== 1 ? "s" : ""}</>}
          {issueCount === 0 && " · No issues"}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <BellButton
          isSnoozed={isSnoozed}
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSnoozeClick(location);
          }}
        />
        <ChevronRight size={18} className="text-copilot-text-tertiary" />
      </div>
    </Link>
  );
};

const Overview = ({ organizationId }: { organizationId: number }) => {
  const mapRef = useRef<MapboxMapHandle>(null);
  const { data: orgData } = useOrganizationMinimal(organizationId);
  const { data: { results: locations } } = useLocations({ filters: { organization_id: organizationId } });
  const [snoozeTarget, setSnoozeTarget] = useState<LocationData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<CopilotStatus | null>(null);
  const [mapVisible, setMapVisible] = useState(false);

  useEffect(() => {
    mapRef.current?.reset();
  }, [organizationId]);

  // Count locations by status
  const counts = {
    critical: locations.filter((l) => l.health_status === HealthStatus.red).length,
    warning: locations.filter((l) => l.health_status === HealthStatus.yellow).length,
    healthy: locations.filter((l) => l.health_status === HealthStatus.green).length,
  };

  // Filter locations
  const filteredLocations = locations.filter((loc) => {
    const matchesSearch = !searchQuery || loc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || healthStatusToCopilot(loc.health_status) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleFilter = (status: CopilotStatus) => {
    setStatusFilter(statusFilter === status ? null : status);
  };

  return (
    <div className="min-h-screen bg-copilot-surface-sunken">
      {/* Header */}
      <header className="bg-white px-[var(--page-padding)] py-[var(--space-md)] border-b border-copilot-neutral-border sticky top-0 z-40">
        <h1 className="text-[var(--heading-page-size)] font-bold leading-[var(--heading-page-lh)] text-copilot-text-primary">
          {orgData.name}
        </h1>
      </header>

      {/* Summary count bar */}
      <div className="bg-white border-b border-copilot-neutral-border px-[var(--page-padding)] py-[var(--space-sm)] flex items-center gap-[var(--space-sm)] sticky top-[61px] z-[39] xl:hidden">
        <span className="text-xs font-medium text-copilot-text-secondary whitespace-nowrap">Filter:</span>
        <StatusBadge
          status="critical"
          interactive
          active={statusFilter === "critical"}
          onClick={() => toggleFilter("critical")}
        >
          {counts.critical} Critical
        </StatusBadge>
        <StatusBadge
          status="warning"
          interactive
          active={statusFilter === "warning"}
          onClick={() => toggleFilter("warning")}
        >
          {counts.warning} Warning
        </StatusBadge>
        <StatusBadge
          status="healthy"
          interactive
          active={statusFilter === "healthy"}
          onClick={() => toggleFilter("healthy")}
        >
          {counts.healthy} Healthy
        </StatusBadge>
      </div>

      {/* Content */}
      <div className="px-[var(--page-padding)]">
        <div className="py-[var(--section-gap)] xl:grid xl:grid-cols-[300px_1fr] xl:gap-[var(--space-lg)]">

          {/* Sidebar (mobile: inline controls, desktop: sidebar) */}
          <aside className="xl:sticky xl:top-[100px] xl:self-start flex flex-col gap-[var(--space-md)] mb-[var(--section-gap)] xl:mb-0">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-copilot-text-tertiary pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search locations..."
                className="w-full h-[var(--touch-target)] pl-10 pr-[var(--space-md)] border-[1.5px] border-copilot-neutral-border rounded-[var(--card-radius)] text-sm bg-white text-copilot-text-primary outline-none focus:border-copilot-brand transition-colors"
              />
            </div>

            {/* Desktop-only filter badges */}
            <div className="hidden xl:flex flex-col gap-1">
              <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary mb-1">
                Status Filter
              </span>
              <StatusBadge status="critical" interactive active={statusFilter === "critical"} onClick={() => toggleFilter("critical")}>
                {counts.critical} Critical
              </StatusBadge>
              <StatusBadge status="warning" interactive active={statusFilter === "warning"} onClick={() => toggleFilter("warning")}>
                {counts.warning} Warning
              </StatusBadge>
              <StatusBadge status="healthy" interactive active={statusFilter === "healthy"} onClick={() => toggleFilter("healthy")}>
                {counts.healthy} Healthy
              </StatusBadge>
            </div>

            {/* Map toggle */}
            <button
              onClick={() => setMapVisible(!mapVisible)}
              className="w-full text-sm font-semibold text-copilot-text-secondary border border-copilot-neutral-border rounded-full px-4 py-2 min-h-[36px] bg-transparent hover:bg-copilot-surface-sunken transition-colors"
            >
              📍 {mapVisible ? "Hide map" : "View map"}
            </button>

            {mapVisible && (
              <div className="relative min-h-[250px] rounded-[var(--card-radius)] overflow-hidden border border-copilot-neutral-border">
                <MapboxMapComponent
                  className="absolute h-full w-full"
                  ref={mapRef}
                  locations={locations.map((loc) => ({
                    lng: loc.longitude,
                    lat: loc.latitude,
                    fillColor: installationHealthStatusFillClass[loc.health_status],
                    tooltip: <span className="text-sm">{loc.name}</span>,
                  }))}
                />
              </div>
            )}
          </aside>

          {/* Location list */}
          <section>
            <div className="flex flex-col gap-[var(--space-sm)] md:grid md:grid-cols-2 md:gap-[var(--space-md)]">
              {filteredLocations.map((loc) => (
                <LocationRow
                  key={loc.id}
                  location={loc}
                  isSnoozed={loc.is_snoozed}
                  onSnoozeClick={setSnoozeTarget}
                />
              ))}
              {filteredLocations.length === 0 && (
                <p className="text-sm text-copilot-text-tertiary col-span-full text-center py-8">
                  No locations match your search.
                </p>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Snooze sheet */}
      {snoozeTarget && (
        <LocationSnoozeSheetWrapper
          locationId={snoozeTarget.id}
          locationName={snoozeTarget.name}
          isSnoozed={snoozeTarget.is_snoozed}
          open={true}
          onOpenChange={(open) => { if (!open) setSnoozeTarget(null); }}
        />
      )}
    </div>
  );
};

export default Overview;
